"use client";

import { KeyRound, LockKeyhole, ShieldCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";

type OwnerAuthMode = "login" | "setup" | "unavailable";

export function OwnerAuthScreen({ mode }: { mode: OwnerAuthMode }) {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const isSetup = mode === "setup";
  const title = isSetup ? "Tạo khóa truy cập" : "Mở Personal OS";
  const description = isSetup
    ? "Khóa này bảo vệ dữ liệu khi ứng dụng được mở qua Internet. Khóa chỉ được lưu dưới dạng băm."
    : "Nhập khóa riêng của anh để tiếp tục.";

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSetup && password !== confirmation) {
      setError("Hai lần nhập khóa chưa giống nhau.");
      passwordRef.current?.focus();
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        isSetup ? "/api/auth/setup" : "/api/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password, ...(isSetup ? { setupToken } : {}) }),
        },
      );
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(payload.error ?? "Không thể mở Personal OS.");
      }
      router.replace("/");
      router.refresh();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Không thể mở Personal OS.",
      );
      passwordRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card" aria-labelledby="auth-title">
        <span className="auth-mark" aria-hidden="true">
          {isSetup ? <ShieldCheck size={30} /> : <LockKeyhole size={30} />}
        </span>
        <p className="eyebrow">Personal OS · Riêng tư</p>
        <h1 id="auth-title">{mode === "unavailable" ? "Chưa bật thiết lập" : title}</h1>

        {mode === "unavailable" ? (
          <div className="auth-unavailable">
            <p>
              Lần thiết lập đầu tiên đang bị khóa an toàn.
            </p>
            <p>Bật mã thiết lập một lần trong môi trường vận hành rồi mở lại trang này.</p>
          </div>
        ) : (
          <>
            <p className="auth-description">{description}</p>
            <form className="auth-form" onSubmit={submit} noValidate>
              {isSetup ? (
                <label className="field">
                  <span>Mã thiết lập một lần</span>
                  <input
                    type="password"
                    value={setupToken}
                    onChange={(event) => setSetupToken(event.target.value)}
                    autoComplete="off"
                    minLength={32}
                    required
                    autoFocus
                  />
                </label>
              ) : null}
              <label className="field">
                <span>Khóa truy cập</span>
                <input
                  ref={passwordRef}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={isSetup ? "new-password" : "current-password"}
                  minLength={12}
                  maxLength={128}
                  required
                  autoFocus={!isSetup}
                  aria-describedby={error ? "auth-error" : "auth-hint"}
                />
              </label>
              {isSetup ? (
                <label className="field">
                  <span>Nhập lại khóa</span>
                  <input
                    type="password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                  />
                </label>
              ) : null}
              <p id="auth-hint" className="auth-hint">
                {isSetup
                  ? "Tối thiểu 12 ký tự. Apple Passwords có thể tạo và đồng bộ giúp anh."
                  : "Phiên đăng nhập được giữ riêng trên thiết bị này trong 30 ngày."}
              </p>
              {error ? (
                <p id="auth-error" className="form-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button className="primary-button auth-submit" type="submit" disabled={submitting}>
                <KeyRound size={18} aria-hidden="true" />
                {submitting
                  ? "Đang bảo vệ…"
                  : isSetup
                    ? "Thiết lập và tiếp tục"
                    : "Đăng nhập"}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
